import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/** AH: the shortest add-to-cart spin: a 300 ms wind-up then cruise at 450 ms a turn, about a turn and a half by 750 ms. */
const AH_MIN_SPIN = 750;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** AH: pending drawer open, waiting for the add-to-cart spin. @type {number | null} */
  #ahSpinTimer = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#ahSpinTimer !== null) clearTimeout(this.#ahSpinTimer);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#ahAfterSpin(event, () => this.#themeDrawer?.open());
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          this.#ahStopSpin(event);
          settle();
          return;
        }

        const openAndSettle = () => {
          this.#ahAfterSpin(event, () => {
            if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
            settle();
          });
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * AH (motion pass, Payne 2026-10-01: "click --> spin --> cart window"): the add-to-cart spin in ah-motion.css runs
   * until the cart is ready, and never less than AH_MIN_SPIN. This waits out the rest of that minimum, then stops the
   * arc (it eases to rest from its current angle and fades) and runs `open`, so the drawer arrives as the arc leaves.
   * No spin running (reduced motion, quick add, an add from anywhere else) means no wait.
   * @param {Event} event
   * @param {() => void} open
   */
  #ahAfterSpin(event, open) {
    if (this.#ahSpinTimer !== null) clearTimeout(this.#ahSpinTimer);
    this.#ahSpinTimer = null;
    const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const button = /** @type {HTMLElement | null | undefined} */ (form?.querySelector('.add-to-cart-button[data-added="true"]'));
    const spin = button?.getAnimations({ subtree: true }).find((animation) => {
      return animation instanceof CSSAnimation && animation.animationName === 'ah-atc-cruise';
    });
    const elapsed = typeof spin?.currentTime === 'number' ? spin.currentTime : null;
    if (!button || elapsed === null) {
      open();
      return;
    }
    const release = () => {
      this.#ahSpinTimer = null;
      // The arc's angle now: 120 deg of wind-up, then cruise turns (the cruise animation's clock includes its delay).
      const now = typeof spin?.currentTime === 'number' ? spin.currentTime : 0;
      const timing = spin?.effect?.getComputedTiming();
      const turn = Number(timing?.duration) || 450;
      const windup = Number(timing?.delay) || 300;
      const angle = now < windup ? 120 * (now / windup) ** 2 : 120 + (((now - windup) % turn) / turn) * 360;
      button.style.setProperty('--ah-spin-from', `${angle}deg`);
      button.dataset.ahSpun = '';
      open();
    };
    const wait = Math.max(0, AH_MIN_SPIN - elapsed);
    if (!wait) release();
    else this.#ahSpinTimer = setTimeout(release, wait);
  }

  /** AH: the add will not open the drawer (an error, or it is already open): still bring the spin to rest. */
  #ahStopSpin(/** @type {Event} */ event) {
    this.#ahAfterSpin(event, () => {});
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
